import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Api,
  CheckCircle,
  Error,
  Visibility,
  VisibilityOff,
  Launch,
  ContentCopy
} from '@mui/icons-material';
import { apiChannelsAPI, inboxesAPI } from '../services/api';

interface APIChannelData {
  id?: number;
  name: string;
  provider: string;
  provider_config: Record<string, any>;
  api_key?: string;
  webhook_url?: string;
  webhook_secret?: string;
  base_url?: string;
  auth_type: string;
  auth_header: string;
  incoming_webhook_path?: string;
  outgoing_webhook_url?: string;
  rate_limit: number;
  retry_attempts: number;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
  supports_media: boolean;
  auto_response_enabled: boolean;
}

interface Provider {
  name: string;
  description: string;
  features: string[];
  setup_complexity: string;
  documentation_url: string;
  limitations?: string;
  use_cases?: string;
}

interface ConfigField {
  type: string;
  label: string;
  required: boolean;
  default?: any;
  description?: string;
  options?: { value: string; label: string; }[];
}

const steps = [
  'Provider Selection',
  'Provider Configuration', 
  'API Settings',
  'Channel Settings',
  'Verification'
];

interface APIChannelConfigProps {
  editMode?: boolean;
  channelId?: number;
  fallbackChannelId?: number; // Main channel ID to use if API channel ID not available
  initialData?: APIChannelData;
  onSave?: (channelData: APIChannelData) => void;
  onCancel?: () => void;
}

const APIChannelConfig: React.FC<APIChannelConfigProps> = ({ 
  editMode = false, 
  channelId,
  fallbackChannelId,
  initialData,
  onSave,
  onCancel 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  
  const [providers, setProviders] = useState<Record<string, Provider>>({});
  const [configFields, setConfigFields] = useState<Record<string, ConfigField>>({});
  const [createdChannel, setCreatedChannel] = useState<any>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  const [formData, setFormData] = useState<APIChannelData>(initialData || {
    name: '',
    provider: 'generic',
    provider_config: {},
    auth_type: 'api_key',
    auth_header: 'Authorization',
    rate_limit: 60,
    retry_attempts: 3,
    greeting_enabled: true,
    greeting_message: 'Hello! Welcome to our API support. How can we help you today?',
    auto_assignment_enabled: true,
    supports_media: true,
    auto_response_enabled: false,
  });

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (formData.provider) {
      loadProviderConfig(formData.provider);
    }
  }, [formData.provider]);

  useEffect(() => {
    console.log('🔍 DEBUG APIChannelConfig useEffect:', { editMode, channelId, fallbackChannelId, initialData });
    if (editMode && channelId) {
      // In edit mode, fetch the actual API channel data
      console.log('🔍 DEBUG: Loading API channel data for ID:', channelId);
      loadAPIChannelData(channelId);
    } else if (editMode && !channelId && fallbackChannelId) {
      // Fallback: try to find/create API channel using main channel ID
      console.log('🔧 DEBUG: No API channel ID, trying fallback with main channel ID:', fallbackChannelId);
      loadOrCreateAPIChannelData(fallbackChannelId);
    } else if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
      if (editMode) {
        setActiveStep(4); // Show completion/view step
      }
    } else if (editMode) {
      console.error('🚨 DEBUG: Edit mode enabled but no channelId or fallbackChannelId provided');
      setError('Unable to load channel data for editing');
    }
  }, [editMode, channelId, fallbackChannelId, initialData]);

  const loadProviders = async () => {
    try {
      const providers = await apiChannelsAPI.getProviders();
      setProviders(providers);
    } catch (error) {
      console.error('Failed to load API providers:', error);
      setError('Failed to load providers');
    }
  };

  const loadProviderConfig = async (provider: string) => {
    try {
      console.log('🔍 DEBUG: Loading provider config for:', provider);
      const config = await apiChannelsAPI.getProviderConfig(provider);
      console.log('✅ DEBUG: Provider config loaded:', config);
      setConfigFields(config.config_fields);
      console.log('✅ DEBUG: Config fields set:', config.config_fields);
    } catch (error) {
      console.error('Failed to load provider config:', error);
      setError('Failed to load provider configuration');
    }
  };

  const loadAPIChannelData = async (channelId: number) => {
    try {
      setLoading(true);
      console.log('🔍 DEBUG: Calling apiChannelsAPI.getChannel with ID:', channelId);
      const channelData = await apiChannelsAPI.getChannel(channelId);
      console.log('✅ DEBUG: Successfully loaded channel data:', channelData);
      
      setFormData({
        id: channelData.id,
        name: channelData.name,
        provider: channelData.provider,
        provider_config: channelData.provider_config || {},
        api_key: channelData.api_key || '',
        webhook_url: channelData.webhook_url || '',
        webhook_secret: channelData.webhook_secret || '',
        base_url: channelData.base_url || '',
        auth_type: channelData.auth_type || 'api_key',
        auth_header: channelData.auth_header || 'Authorization',
        incoming_webhook_path: channelData.incoming_webhook_path || '',
        outgoing_webhook_url: channelData.outgoing_webhook_url || '',
        rate_limit: channelData.rate_limit || 60,
        retry_attempts: channelData.retry_attempts || 3,
        greeting_enabled: channelData.greeting_enabled ?? true,
        greeting_message: channelData.greeting_message || 'Hello! How can we help you today?',
        auto_assignment_enabled: channelData.auto_assignment_enabled ?? true,
        supports_media: channelData.supports_media ?? true,
        auto_response_enabled: channelData.auto_response_enabled ?? false,
      });
      
      console.log('✅ DEBUG: Form data set with:', {
        provider: channelData.provider,
        provider_config: channelData.provider_config,
        base_url: channelData.base_url
      });
      
      // Load provider config for the loaded provider
      if (channelData.provider) {
        await loadProviderConfig(channelData.provider);
      }
      
      // Start from first step to allow editing
      setActiveStep(0);
      
    } catch (error: any) {
      console.error('❌ DEBUG: Failed to load API channel data:', error);
      console.error('❌ DEBUG: Error response:', error.response);
      console.error('❌ DEBUG: Error status:', error.response?.status);
      console.error('❌ DEBUG: Error data:', error.response?.data);
      
      // If API channel doesn't exist (404), treat as new channel creation
      if (error.response?.status === 404 || error.response?.data?.detail === 'API channel not found') {
        console.log('🔄 DEBUG: API channel not found, treating as new channel creation');
        // Reset to step 0 for new channel setup
        setActiveStep(0);
        // Clear error as this is expected for new channels
        setError(null);
      } else {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to load API channel data';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOrCreateAPIChannelData = async (mainChannelId: number) => {
    try {
      setLoading(true);
      console.log('🔧 Attempting to fix and load API channel for main channel ID:', mainChannelId);
      
      // First, call the fix endpoint to ensure API channel records exist
      const fixResponse = await inboxesAPI.fixAPIChannels();
      console.log('✅ Fix API channels response:', fixResponse);
      
      // Now try to find the API channel by searching through all API channels
      const apiChannels = await apiChannelsAPI.getChannels();
      
      // Find the API channel that belongs to our main channel
      const matchingAPIChannel = apiChannels.find((apiChannel: any) => 
        apiChannel.channel_id === mainChannelId
      );
      
      if (matchingAPIChannel) {
        console.log('✅ Found matching API channel:', matchingAPIChannel);
        
        // Load this API channel data
        setFormData({
          id: matchingAPIChannel.id,
          name: matchingAPIChannel.name,
          provider: matchingAPIChannel.provider,
          provider_config: matchingAPIChannel.provider_config || {},
          api_key: matchingAPIChannel.api_key || '',
          webhook_url: matchingAPIChannel.webhook_url || '',
          webhook_secret: matchingAPIChannel.webhook_secret || '',
          base_url: matchingAPIChannel.base_url || '',
          auth_type: matchingAPIChannel.auth_type || 'api_key',
          auth_header: matchingAPIChannel.auth_header || 'Authorization',
          incoming_webhook_path: matchingAPIChannel.incoming_webhook_path || '',
          outgoing_webhook_url: matchingAPIChannel.outgoing_webhook_url || '',
          rate_limit: matchingAPIChannel.rate_limit || 60,
          retry_attempts: matchingAPIChannel.retry_attempts || 3,
          greeting_enabled: matchingAPIChannel.greeting_enabled ?? true,
          greeting_message: matchingAPIChannel.greeting_message || 'Hello! How can we help you today?',
          auto_assignment_enabled: matchingAPIChannel.auto_assignment_enabled ?? true,
          supports_media: matchingAPIChannel.supports_media ?? true,
          auto_response_enabled: matchingAPIChannel.auto_response_enabled ?? false,
        });
        
        // Load provider config
        if (matchingAPIChannel.provider) {
          await loadProviderConfig(matchingAPIChannel.provider);
        }
        
        // Start from final step to show current configuration
        setActiveStep(4);
      } else {
        console.error('❌ No matching API channel found after fix attempt');
        setError('Unable to find or create API channel configuration');
      }
      
    } catch (error: any) {
      console.error('❌ Failed to load or create API channel data:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to load or create API channel data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('provider_config.')) {
      const configField = field.replace('provider_config.', '');
      setFormData(prev => ({
        ...prev,
        provider_config: {
          ...prev.provider_config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Provider Selection
        return !!formData.provider;
      case 1: // Provider Configuration
        return Object.entries(configFields).every(([key, config]) => {
          if (!config.required) return true;
          const value = formData.provider_config[key];
          return value !== undefined && value !== null && value !== '';
        });
      case 2: // API Settings
        return true; // API settings are optional
      case 3: // Channel Settings
        return !!formData.name && !!formData.greeting_message;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields before proceeding.');
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      // Determine if this is an update or create based on formData.id
      const isUpdate = editMode && (channelId || formData.id);
      const updateId = channelId || formData.id;
      
      console.log('🔍 Submit Debug:', { editMode, channelId, formDataId: formData.id, isUpdate, updateId });
      
      if (isUpdate && updateId) {
        // Update existing channel
        console.log(`📝 Updating API channel with ID: ${updateId}`);
        response = await apiChannelsAPI.updateChannel(updateId, formData);
        setSuccess('API channel updated successfully!');
        if (onSave) {
          onSave(response);
        }
      } else {
        // Create new channel  
        console.log('➕ Creating new API channel');
        response = await apiChannelsAPI.createChannel(formData);
        setCreatedChannel(response);
        setSuccess('API channel created successfully!');
        setActiveStep(4);
      }
    } catch (error: any) {
      const action = editMode ? 'update' : 'create';
      setError(error.response?.data?.detail || `Failed to ${action} API channel`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    const channelId = editMode ? formData.id : createdChannel?.id;
    if (!channelId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await apiChannelsAPI.verifyChannel(channelId);
      setVerificationResult(result);
      if (result.verified) {
        setSuccess('API channel verified successfully!');
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!formData.base_url) {
      setError('Please provide a base URL to test connection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await apiChannelsAPI.testConnection({
        base_url: formData.base_url,
        auth_type: formData.auth_type,
        api_key: formData.api_key,
        auth_header: formData.auth_header
      });
      
      if (result.success) {
        setSuccess('Connection test successful!');
      } else {
        setError(result.error || 'Connection test failed');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const renderProviderSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select API Provider Type
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(providers).map(([key, provider]) => (
          <Grid item xs={12} md={6} key={key}>
            <Card 
              variant={formData.provider === key ? 'elevation' : 'outlined'}
              sx={{ 
                cursor: 'pointer',
                border: formData.provider === key ? '2px solid #1976d2' : undefined
              }}
              onClick={() => handleInputChange('provider', key)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Api sx={{ mr: 1 }} />
                  <Typography variant="h6">{provider.name}</Typography>
                </Box>
                <Typography color="text.secondary" paragraph>
                  {provider.description}
                </Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {provider.features.map((feature, index) => (
                      <Chip key={index} label={feature} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Setup: {provider.setup_complexity}
                </Typography>
                {provider.use_cases && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Use cases: {provider.use_cases}
                  </Typography>
                )}
                {provider.limitations && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {provider.limitations}
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Launch />}
                  sx={{ mt: 1 }}
                  href={provider.documentation_url}
                  target="_blank"
                >
                  Documentation
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderConfigField = (key: string, config: ConfigField) => {
    const value = formData.provider_config[key] || config.default || '';
    
    if (config.type === 'select') {
      return (
        <FormControl fullWidth key={key}>
          <InputLabel>{config.label}</InputLabel>
          <Select
            value={value}
            label={config.label}
            onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
            required={config.required}
          >
            {config.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (config.type === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.checked)}
            />
          }
          label={config.label}
        />
      );
    }

    if (config.type === 'textarea') {
      return (
        <TextField
          key={key}
          label={config.label}
          value={value}
          onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
          required={config.required}
          multiline
          rows={3}
          fullWidth
          helperText={config.description}
        />
      );
    }

    return (
      <TextField
        key={key}
        label={config.label}
        value={value}
        onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
        required={config.required}
        fullWidth
        type={config.type === 'password' ? (showPasswords[key] ? 'text' : 'password') : config.type}
        helperText={config.description}
        InputProps={config.type === 'password' ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => togglePasswordVisibility(key)} edge="end">
                {showPasswords[key] ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        } : undefined}
      />
    );
  };

  const renderProviderConfiguration = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure {providers[formData.provider]?.name || 'API'} Provider
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Configure your API provider settings. Required fields must be filled.
        </Typography>
      </Alert>
      
      <Grid container spacing={2}>
        {Object.entries(configFields).map(([key, config]) => (
          <Grid item xs={12} key={key}>
            {renderConfigField(key, config)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderAPISettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        API Configuration Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Base URL"
            value={formData.base_url || ''}
            onChange={(e) => handleInputChange('base_url', e.target.value)}
            fullWidth
            placeholder="https://api.example.com"
            helperText="Base URL of your API endpoint"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Authentication Type</InputLabel>
            <Select
              value={formData.auth_type}
              label="Authentication Type"
              onChange={(e) => handleInputChange('auth_type', e.target.value)}
            >
              <MenuItem value="api_key">API Key</MenuItem>
              <MenuItem value="bearer">Bearer Token</MenuItem>
              <MenuItem value="basic">Basic Auth</MenuItem>
              <MenuItem value="none">No Authentication</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Auth Header Name"
            value={formData.auth_header}
            onChange={(e) => handleInputChange('auth_header', e.target.value)}
            fullWidth
            helperText="Header name for authentication (e.g., Authorization)"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Rate Limit (per minute)"
            value={formData.rate_limit}
            onChange={(e) => handleInputChange('rate_limit', parseInt(e.target.value))}
            type="number"
            fullWidth
            helperText="Maximum requests per minute"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Webhook URL"
            value={formData.webhook_url || ''}
            onChange={(e) => handleInputChange('webhook_url', e.target.value)}
            fullWidth
            placeholder="https://your-app.com/webhook"
            helperText="URL to receive incoming messages (optional)"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Webhook Secret"
            value={formData.webhook_secret || ''}
            onChange={(e) => handleInputChange('webhook_secret', e.target.value)}
            type={showPasswords['webhook_secret'] ? 'text' : 'password'}
            fullWidth
            helperText="Secret key for webhook signature verification"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => togglePasswordVisibility('webhook_secret')} edge="end">
                    {showPasswords['webhook_secret'] ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.supports_media}
                onChange={(e) => handleInputChange('supports_media', e.target.checked)}
              />
            }
            label="Supports Media Messages"
          />
        </Grid>

        {/* Test Connection Button */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={loading || !formData.base_url}
              startIcon={loading ? <CircularProgress size={20} /> : <Api />}
            >
              {loading ? 'Testing...' : 'Test Connection'}
            </Button>
            {formData.base_url && (
              <Typography variant="caption" color="text.secondary">
                This will test the connection to: {formData.base_url}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderChannelSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Channel Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Channel Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            fullWidth
            helperText="A descriptive name for your API channel"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.greeting_enabled}
                onChange={(e) => handleInputChange('greeting_enabled', e.target.checked)}
              />
            }
            label="Enable Greeting Message"
          />
        </Grid>

        {formData.greeting_enabled && (
          <Grid item xs={12}>
            <TextField
              label="Greeting Message"
              value={formData.greeting_message}
              onChange={(e) => handleInputChange('greeting_message', e.target.value)}
              required
              multiline
              rows={3}
              fullWidth
              helperText="Message sent when a conversation starts"
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_assignment_enabled}
                onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
              />
            }
            label="Auto-assign Conversations"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_response_enabled}
                onChange={(e) => handleInputChange('auto_response_enabled', e.target.checked)}
              />
            }
            label="Enable Auto-Response"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderVerification = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        API Channel Created
      </Typography>
      
      {createdChannel && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {createdChannel.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Provider:</Typography>
                <Typography>{createdChannel.provider}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Box display="flex" alignItems="center">
                  {createdChannel.is_verified ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="warning" sx={{ mr: 1 }} />
                  )}
                  <Typography>
                    {createdChannel.is_verified ? 'Verified' : 'Not Verified'}
                  </Typography>
                </Box>
              </Grid>
              {createdChannel.webhook_url && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Webhook URL:</Typography>
                  <Box display="flex" alignItems="center">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        padding: '4px 8px',
                        borderRadius: 1,
                        flexGrow: 1,
                        mr: 1
                      }}
                    >
                      {createdChannel.webhook_url}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(createdChannel.webhook_url)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Button
        variant="contained"
        onClick={handleVerify}
        disabled={loading || !createdChannel}
        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        fullWidth
      >
        {loading ? 'Verifying...' : 'Test Connection'}
      </Button>

      {verificationResult && (
        <Alert 
          severity={verificationResult.verified ? 'success' : 'error'} 
          sx={{ mt: 2 }}
        >
          {verificationResult.verified ? (
            'API channel verified successfully!'
          ) : (
            `Verification failed: ${verificationResult.error}`
          )}
        </Alert>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        overflow: 'hidden',
      }}
    >
      <Box display="flex" alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
        <Api sx={{ mr: 2 }} />
        <Typography variant="h5">
          {editMode ? 'Edit API Channel' : 'Setup API Channel'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {index === 0 && renderProviderSelection()}
                {index === 1 && renderProviderConfiguration()}
                {index === 2 && renderAPISettings()}
                {index === 3 && renderChannelSettings()}
                {index === 4 && renderVerification()}
                
                <Box sx={{ mb: 2, mt: 2 }}>
                  <div>
                    {index < 4 && (
                      <Button
                        variant="contained"
                        onClick={index === 3 ? handleSubmit : handleNext}
                        disabled={loading || !validateStep(index)}
                        sx={{ mt: 1, mr: 1 }}
                        startIcon={loading ? <CircularProgress size={20} /> : undefined}
                      >
                        {loading ? 'Processing...' : (index === 3 ? (editMode ? 'Update Channel' : 'Create Channel') : 'Continue')}
                      </Button>
                    )}
                    {index > 0 && index < 4 && (
                      <Button
                        disabled={loading}
                        onClick={handleBack}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </Button>
                    )}
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Paper>
  );
};

export default APIChannelConfig;